import { useApolloClient } from '@apollo/client';
import { Button, message, Space } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type AudiFinancingModuleSpecsFragment, type SdmModuleSpecsFragment } from '../../../../../api/fragments';
import {
    UpdateSalesForceSettingDocument,
    type UpdateSalesForceSettingMutation,
    type UpdateSalesForceSettingMutationVariables,
} from '../../../../../api/mutations/updateSalesForceSetting';
import { type SalesForceSettingInput } from '../../../../../api/types';
import DrawerWithAutoWidth from '../../../../../components/DrawerWithAutoWidth';
import Form from '../../../../../components/fields/Form';
import InputField from '../../../../../components/fields/InputField';
import PasswordField from '../../../../../components/fields/PasswordField';
import SingleSecretUploadField from '../../../../../components/fields/SingleSecretUploadField';
import useHandleError from '../../../../../utilities/useHandleError';
import useValidator from '../../../../../utilities/useValidator';
import validators from '../../../../../utilities/validators';

export type SalesForceFormDrawerProps = {
    visible: boolean;
    onClose: () => void;
    module: SdmModuleSpecsFragment | AudiFinancingModuleSpecsFragment;
};

const validator = validators.compose(
    validators.requiredString('baseUrl'),
    validators.requiredString('clientId'),
    validators.requiredString('clientSecret'),
    validators.requiredString('grantType')
);

const SalesForceFormDrawer = ({ module, visible, onClose }: SalesForceFormDrawerProps) => {
    const { t } = useTranslation(['sdmModuleDetails']);
    const apolloClient = useApolloClient();

    const initialValues: SalesForceSettingInput = useMemo(() => {
        const salesForceSetting = pick(
            ['baseUrl', 'baseInsuranceUrl', 'clientId', 'grantType', 'authority', 'publicCert'],
            module.salesForceSetting
        );

        return {
            ...salesForceSetting,
            clientSecret: '',
            privateKey: '',
        };
    }, [module]);

    const onSubmit = useHandleError(
        async (settings: SalesForceSettingInput) => {
            message.loading({
                content: t('sdmModuleDetails:messages.updatingSalesForce'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateSalesForceSettingMutation, UpdateSalesForceSettingMutationVariables>({
                    mutation: UpdateSalesForceSettingDocument,
                    variables: { financingModuleId: module.id, settings },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('sdmModuleDetails:messages.updateSuccess'),
                key: 'primary',
            });

            // close the drawer
            onClose();
        },
        [apolloClient, module.id, onClose, t]
    );

    const validate = useValidator(validator);

    return (
        <DrawerWithAutoWidth
            extra={
                <Space>
                    <Button onClick={onClose}>{t('sdmModuleDetails:actions.cancel')}</Button>
                    <Button form="updateSalesForce" htmlType="submit" type="primary">
                        {t('sdmModuleDetails:actions.update')}
                    </Button>
                </Space>
            }
            onClose={onClose}
            title={t('sdmModuleDetails:salesForce.title.update')}
            visible={visible}
            destroyOnClose
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="updateSalesForce" name="updateSalesForce" onSubmitCapture={handleSubmit}>
                        <InputField
                            {...t('sdmModuleDetails:fields.salesForce.baseUrl', { returnObjects: true })}
                            name="baseUrl"
                            required
                        />
                        <InputField
                            {...t('sdmModuleDetails:fields.salesForce.baseInsuranceUrl', { returnObjects: true })}
                            name="baseInsuranceUrl"
                        />
                        <InputField
                            {...t('sdmModuleDetails:fields.salesForce.clientId', { returnObjects: true })}
                            name="clientId"
                            required
                        />
                        <PasswordField
                            {...t('sdmModuleDetails:fields.salesForce.clientSecret', { returnObjects: true })}
                            name="clientSecret"
                            required
                        />
                        <InputField
                            {...t('sdmModuleDetails:fields.salesForce.grantType', { returnObjects: true })}
                            name="grantType"
                            required
                        />
                        <SingleSecretUploadField
                            {...t('moduleDetails:fields.authority', { returnObjects: true })}
                            name="authority"
                        />
                        <SingleSecretUploadField
                            {...t('moduleDetails:fields.publicCert', { returnObjects: true })}
                            name="publicCert"
                        />
                        <SingleSecretUploadField
                            {...t('moduleDetails:fields.privateKey', { returnObjects: true })}
                            name="privateKey"
                        />
                    </Form>
                )}
            </Formik>
        </DrawerWithAutoWidth>
    );
};

export default SalesForceFormDrawer;

export const useSalesForceFormDrawer = () => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: (module: SdmModuleSpecsFragment | AudiFinancingModuleSpecsFragment) => (
            <SalesForceFormDrawer module={module} onClose={actions.close} visible={visible} />
        ),
    };
};
